import {UserEntity} from '@dropDesk/domain/entities/user/user.entity';
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import {BaseMessageEntity} from "@dropDesk/domain/entities/messages/base_message_entity";
import {
    AudioMessageEntity, ContactMessageEntity,
    DocumentMessageEntity,
    ImageMessageEntity, LocationMessageEntity,
    MessageChatType,
    MessageStatus, ReactionMessageEntity, ReplyEntity, StickerMessageEntity, SystemMessageEntity,
    TextMessageEntity, VideoMessageEntity
} from "@dropDesk/domain/entities/ticket/message/external/message_entity";
import {UploadOptionsEntity} from "@dropDesk/domain/entities/ticket/message/upload_message.entity";
import {MessagePosition, StatusMessageError} from "@dropDesk/domain/entities/messages/message_enums";
import {TicketEntity} from "@dropDesk/domain/entities/ticket/ticket.entity";
import {TicketMessageEntity} from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";

export class InternalChatMessageEntity extends BaseMessageEntity {
    idInternalChat!: string;
    internalChat?: InternalChatEntity;

    public prepareToSend(): InternalChatMessageEntity {
        const newMessage: InternalChatMessageEntity = this.copyWith(this);
        delete newMessage.internalChat;
        delete newMessage.sender;
        delete newMessage.uploadOptions;
        delete newMessage.updatedAt;
        delete newMessage.showOriginal;
        delete newMessage.loaded;
        return newMessage;
    }

    public removeUploadOptions(): InternalChatMessageEntity {
        const newMessage: InternalChatMessageEntity = this.copyWith(this);
        delete newMessage.uploadOptions;
        return newMessage;
    }

    public getPathToUpload(): string {
        const internalChatBasePath = `companies/${this.idCompany}/internalChats/${this.idInternalChat}`;
        return super.getPathToUpload(internalChatBasePath);
    }

    public getMessagePreventDefaultCache(newMessage: InternalChatMessageEntity): InternalChatMessageEntity {
        return this.copyWith({
            blobURL: this.blobURL,
            uploadOptions: this.uploadOptions,
            ...newMessage,
            readers: Object.keys(this.readers ?? {}).length > Object.keys(newMessage.readers ?? {}).length ? this.readers : newMessage.readers,
            received: Object.keys(this.received ?? {}).length > Object.keys(newMessage.received ?? {}).length ? this.received : newMessage.received
        });
    }

    public setEditableText(editText: string): InternalChatMessageEntity {
        super.setEditableText(editText);
        return this;
    }

    public static fromJson(json: Record<string, any>): InternalChatMessageEntity {
        return new InternalChatMessageEntity({
            id: json['id'],
            idCompany: json['idCompany'],
            idInternalChat: json['idInternalChat'],
            internalChat: json['internalChat'] ? InternalChatEntity.fromJson(json['internalChat']) : undefined,
            idExternal: json['idExternal'],
            type: json['type'],
            senderId: json['senderId'],
            sender: json['sender'] ? UserEntity.fromJson(json['sender']) : undefined,
            deleted: json['deleted'],
            isPrivate: json['isPrivate'],
            users: json['users'],
            received: json['received'],
            readers: json['readers'],
            createdAt: json['createdAt'],
            updatedAt: json['updatedAt'],
            createdAtLocal: json['createdAtLocal'],
            position: json['position'],
            automatic: json['automatic'],
            showOriginal: json['showOriginal'],
            loaded: json['loaded'],
            statusError: json['statusError'],
            fromMe: json['fromMe'] ?? false,
            participantId: json['participantId'] ?? null,
            pushName: json['pushName'] ?? null,
            status: json['status'],
            blobURL: json['blobURL'],
            textMessage: json['textMessage'] ? new TextMessageEntity({
                ...json['textMessage']
            }) : null,
            systemMessage: json['systemMessage'] ? new SystemMessageEntity({
                ...json['systemMessage']
            }) : null,
            imageMessage: json['imageMessage'] ? new ImageMessageEntity({
                ...json['imageMessage']
            }) : null,
            videoMessage: json['videoMessage'] ? new VideoMessageEntity({
                ...json['videoMessage']
            }) : null,
            documentMessage: json['documentMessage'] ? new DocumentMessageEntity({
                ...json['documentMessage']
            }) : null,
            locationMessage: json['locationMessage'] ? new LocationMessageEntity({
                ...json['locationMessage']
            }) : null,
            stickerMessage: json['stickerMessage'] ? new StickerMessageEntity({
                ...json['stickerMessage']
            }) : null,
            audioMessage: json['audioMessage'] ? new AudioMessageEntity({
                ...json['audioMessage']
            }) : null,
            reactionMessage: json['reactionMessage'] ? new ReactionMessageEntity({
                ...json['reactionMessage']
            }) : null,
            contactMessage: json['contactMessage'] ? (json['contactMessage'] as any[]).map((entry) => new ContactMessageEntity({
                ...entry
            })) : null,
            reply: json['reply'] ? new ReplyEntity({
                ...json['reply']
            }) : undefined,
            outOfBusinessHour: json['outOfBusinessHour'] ?? false,
            edited: json['edited'] ?? false,
            fromSystem: json['fromSystem'] ?? false,
            uploadOptions: json['uploadOptions'],
        });
    }

    constructor({
                    id,
                    idCompany,
                    idInternalChat,
                    internalChat,
                    idExternal,
                    type,
                    senderId,
                    sender,
                    deleted,
                    isPrivate,
                    users,
                    received,
                    readers,
                    createdAt,
                    updatedAt,
                    createdAtLocal,
                    status,
                    uploadOptions,
                    fromMe,
                    participantId,
                    pushName,
                    textMessage,
                    systemMessage,
                    imageMessage,
                    videoMessage,
                    documentMessage,
                    locationMessage,
                    stickerMessage,
                    audioMessage,
                    reactionMessage,
                    contactMessage,
                    blobURL,
                    reply,
                    outOfBusinessHour,
                    edited,
                    fromSystem,
                    position,
                    automatic,
                    showOriginal,
                    loaded,
                    statusError
                }: {
        id: string;
        idCompany: string;
        idInternalChat: string;
        internalChat?: InternalChatEntity;
        idExternal?: string;
        type: MessageChatType;
        senderId: string;
        sender?: UserEntity;
        deleted: boolean;
        isPrivate: boolean;
        users?: Record<string, unknown>;
        received?: Record<string, unknown>;
        readers?: Record<string, unknown>;
        createdAt?: string;
        updatedAt?: string;
        createdAtLocal: string;
        status: MessageStatus;
        uploadOptions?: UploadOptionsEntity;
        fromMe: boolean;
        participantId?: string | null;
        pushName?: string | null;
        textMessage?: TextMessageEntity | null;
        systemMessage?: SystemMessageEntity | null;
        imageMessage?: ImageMessageEntity | null;
        videoMessage?: VideoMessageEntity | null;
        documentMessage?: DocumentMessageEntity | null;
        locationMessage?: LocationMessageEntity | null;
        stickerMessage?: StickerMessageEntity | null;
        audioMessage?: AudioMessageEntity | null;
        reactionMessage?: ReactionMessageEntity | null;
        contactMessage?: ContactMessageEntity[] | null;
        blobURL?: string | null;
        reply?: ReplyEntity | null;
        outOfBusinessHour?: boolean;
        edited?: boolean;
        fromSystem?: boolean;
        automatic?: boolean;
        showOriginal?: boolean;
        loaded?: boolean;
        statusError?: StatusMessageError | null;
        position?: MessagePosition;
    }) {
        super({
            id,
            idCompany,
            idExternal,
            type,
            senderId,
            sender,
            deleted,
            isPrivate,
            users,
            received,
            readers,
            createdAt,
            updatedAt,
            createdAtLocal,
            status,
            uploadOptions,
            fromMe,
            participantId,
            pushName,
            textMessage,
            systemMessage,
            imageMessage,
            videoMessage,
            documentMessage,
            locationMessage,
            stickerMessage,
            audioMessage,
            reactionMessage,
            contactMessage,
            blobURL,
            reply,
            outOfBusinessHour,
            edited,
            fromSystem,
            automatic,
            showOriginal,
            loaded,
            statusError,
            position
        });
        this.idInternalChat = idInternalChat;
        this.internalChat = internalChat;
    }

    copyWith({
                 id,
                 idCompany,
                 idInternalChat,
                 internalChat,
                 idExternal,
                 type,
                 senderId,
                 sender,
                 deleted,
                 isPrivate,
                 users,
                 received,
                 readers,
                 createdAt,
                 updatedAt,
                 createdAtLocal,
                 status,
                 uploadOptions,
                 fromMe,
                 participantId,
                 pushName,
                 textMessage,
                 systemMessage,
                 imageMessage,
                 videoMessage,
                 documentMessage,
                 locationMessage,
                 stickerMessage,
                 audioMessage,
                 reactionMessage,
                 contactMessage,
                 blobURL,
                 reply,
                 outOfBusinessHour,
                 edited,
                 fromSystem,
                 automatic,
                 showOriginal,
                 loaded,
                 statusError,
                 replaceReplyIfNull,
                 position
             }: {
        id?: string;
        idCompany?: string;
        idInternalChat?: string;
        internalChat?: InternalChatEntity;
        idExternal?: string;
        description?: string;
        type?: MessageChatType;
        senderId?: string;
        sender?: UserEntity;
        deleted?: boolean;
        isPrivate?: boolean;
        users?: Record<string, unknown>;
        received?: Record<string, unknown>;
        readers?: Record<string, unknown>;
        createdAt?: string;
        updatedAt?: string;
        createdAtLocal?: string;
        status?: MessageStatus;
        uploadOptions?: UploadOptionsEntity;
        fromMe?: boolean;
        participantId?: string | null;
        pushName?: string | null;
        textMessage?: TextMessageEntity | null;
        systemMessage?: SystemMessageEntity | null;
        imageMessage?: ImageMessageEntity | null;
        videoMessage?: VideoMessageEntity | null;
        documentMessage?: DocumentMessageEntity | null;
        locationMessage?: LocationMessageEntity | null;
        stickerMessage?: StickerMessageEntity | null;
        audioMessage?: AudioMessageEntity | null;
        reactionMessage?: ReactionMessageEntity | null;
        contactMessage?: ContactMessageEntity[] | null;
        blobURL?: string | null;
        reply?: ReplyEntity | null;
        replaceReplyIfNull?: boolean | null
        outOfBusinessHour?: boolean;
        edited?: boolean;
        fromSystem?: boolean;
        automatic?: boolean;
        showOriginal?: boolean;
        loaded?: boolean;
        statusError?: StatusMessageError | null;
        position?: MessagePosition;
    }): InternalChatMessageEntity {
        return new InternalChatMessageEntity({
            id: id ?? this.id,
            idCompany: idCompany ?? this.idCompany,
            idInternalChat: idInternalChat ?? this.idInternalChat,
            internalChat: internalChat ?? this.internalChat,
            idExternal: idExternal ?? this.idExternal,
            type: type ?? this.type,
            senderId: senderId ?? this.senderId,
            sender: sender ?? this.sender,
            deleted: deleted ?? this.deleted,
            isPrivate: isPrivate ?? this.isPrivate,
            users: users ?? this.users,
            received: received ?? this.received,
            readers: readers ?? this.readers,
            createdAt: createdAt ?? this.createdAt,
            updatedAt: updatedAt ?? this.updatedAt,
            createdAtLocal: createdAtLocal ?? this.createdAtLocal,
            status: status ?? this.status,
            uploadOptions: uploadOptions ?? this.uploadOptions,
            fromMe: fromMe ?? this.fromMe,
            participantId: participantId ?? this.participantId,
            pushName: pushName ?? this.pushName,
            textMessage: textMessage ?? this.textMessage,
            systemMessage: systemMessage ?? this.systemMessage,
            imageMessage: imageMessage ?? this.imageMessage,
            videoMessage: videoMessage ?? this.videoMessage,
            documentMessage: documentMessage ?? this.documentMessage,
            locationMessage: locationMessage ?? this.locationMessage,
            stickerMessage: stickerMessage ?? this.stickerMessage,
            audioMessage: audioMessage ?? this.audioMessage,
            reactionMessage: reactionMessage ?? this.reactionMessage,
            contactMessage: contactMessage ?? this.contactMessage,
            reply: replaceReplyIfNull ? reply : this.reply,
            outOfBusinessHour: outOfBusinessHour ?? this.outOfBusinessHour,
            edited: edited ?? this.edited,
            fromSystem: fromSystem ?? this.fromSystem,
            automatic: automatic ?? this.automatic,
            showOriginal: showOriginal ?? this.showOriginal,
            loaded: loaded ?? this.loaded,
            statusError: statusError !== undefined ? statusError : this.statusError,
            blobURL: blobURL ?? this.blobURL,
            position: position ?? this.position,
        })
    }

}

export class BodyMessage {
    internalChat!: InternalChatEntity;
    sender!: UserEntity;
    message!: InternalChatMessageEntity;

    constructor({
                    internalChat,
                    sender,
                    message,
                }: {
        internalChat: InternalChatEntity;
        sender: UserEntity;
        message: InternalChatMessageEntity;
    }) {
        Object.assign(this, {
            internalChat,
            sender,
            message
        });
    }

}